import type { TranscriptHighlight } from './types';
import Logger from 'utils/logger';

const STORAGE_PREFIX = 'rt-notes';
const DEFAULT_MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000; // 7 days

function storageKey(userId: string, caseId: string, sessionKey: string): string {
  return `${STORAGE_PREFIX}:${userId}:${caseId}:${sessionKey}`;
}

function safeGetJSON<T>(key: string, defaultValue: T): T {
  try {
    const item = localStorage.getItem(key);
    return item ? JSON.parse(item) : defaultValue;
  } catch (err) {
    Logger.ERROR('Failed to read localStorage', key, err);
    return defaultValue;
  }
}

function safeSetJSON(key: string, value: unknown): void {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch (err) {
    Logger.ERROR('Failed to write localStorage', key, err);
    try {
      localStorage.removeItem(key);
    } catch {
      // ignore cleanup failure
    }
  }
}

export function getLocalNotes(
  userId: string,
  caseId: string,
  sessionKey: string,
): TranscriptHighlight[] {
  return safeGetJSON<TranscriptHighlight[]>(storageKey(userId, caseId, sessionKey), []);
}

export function saveLocalNote(
  userId: string,
  caseId: string,
  sessionKey: string,
  highlight: TranscriptHighlight,
): void {
  const key = storageKey(userId, caseId, sessionKey);
  const existing = safeGetJSON<TranscriptHighlight[]>(key, []);
  const idx = existing.findIndex((h) => h.highlightId === highlight.highlightId);
  const entry: TranscriptHighlight = { ...highlight, isLocalOnly: true, savedAt: Date.now() };
  if (idx >= 0) {
    existing[idx] = entry;
  } else {
    existing.push(entry);
  }
  safeSetJSON(key, existing);
}

export function removeLocalNote(
  userId: string,
  caseId: string,
  sessionKey: string,
  highlightId: string,
): void {
  const key = storageKey(userId, caseId, sessionKey);
  const existing = safeGetJSON<TranscriptHighlight[]>(key, []);
  const filtered = existing.filter((h) => h.highlightId !== highlightId);
  if (filtered.length !== existing.length) {
    if (filtered.length === 0) {
      try {
        localStorage.removeItem(key);
      } catch {
        // ignore
      }
    } else {
      safeSetJSON(key, filtered);
    }
  }
}

export function clearStaleLocalNotes(
  userId: string,
  caseId: string,
  sessionKey: string,
  maxAgeMs: number = DEFAULT_MAX_AGE_MS,
): void {
  const key = storageKey(userId, caseId, sessionKey);
  const existing = safeGetJSON<TranscriptHighlight[]>(key, []);
  if (existing.length === 0) return;
  const cutoff = Date.now() - maxAgeMs;
  const fresh = existing.filter((h) => (h.savedAt ?? h.createdAt) > cutoff);
  if (fresh.length === existing.length) return;
  if (fresh.length === 0) {
    try {
      localStorage.removeItem(key);
    } catch {
      // ignore
    }
  } else {
    safeSetJSON(key, fresh);
  }
}
